import { TerminalRouteStatus } from '../generated/graphql';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StartIcon from '@mui/icons-material/Start';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import FmdBadIcon from '@mui/icons-material/FmdBad';
import RadioButtonUncheckedOutlinedIcon from '@mui/icons-material/RadioButtonUncheckedOutlined';

/**
 * Centralized terminal status options with generated enum values.
 * Ensures consistency across all status displays and configurations.
 */
export const TERMINAL_STATUS_OPTIONS = [
  {
    value: TerminalRouteStatus.NonArrival,
    label: 'Non Arrival',
  },
  {
    value: TerminalRouteStatus.Arrived,
    label: 'Arrived',
  },
  {
    value: TerminalRouteStatus.Ongoing,
    label: 'Ongoing',
  },
  {
    value: TerminalRouteStatus.ReadyForPu,
    label: 'Ready for PU',
  },
  {
    value: TerminalRouteStatus.Done,
    label: 'Done',
  },
  {
    value: TerminalRouteStatus.Pickup,
    label: 'Pick up',
  },
  {
    value: TerminalRouteStatus.HandedOver,
    label: 'Hand over',
  },
  {
    value: TerminalRouteStatus.Departure,
    label: 'Departure',
  },
] as const;

/**
 * Terminal status options with UI icons for modals and displays.
 * Separated from basic options to avoid importing UI dependencies everywhere.
 */
export const TERMINAL_STATUS_OPTIONS_WITH_ICONS = [
  {
    status: TerminalRouteStatus.NonArrival,
    label: 'Non Arrival',
    color: '#e78849',
    icon: BlockOutlinedIcon,
    rotate: 0,
  },
  {
    status: TerminalRouteStatus.Arrived,
    label: 'Arrived',
    color: '#709933',
    icon: PlaceOutlinedIcon,
    rotate: 0,
  },
  {
    status: TerminalRouteStatus.Ongoing,
    label: 'Ongoing',
    color: '#f5bc43',
    icon: AutorenewOutlinedIcon,
    rotate: 0,
  },
  {
    status: TerminalRouteStatus.ReadyForPu,
    label: 'Ready for PU',
    color: '#d3e01b',
    icon: CheckCircleIcon,
    rotate: 0,
  },
  {
    status: TerminalRouteStatus.Done,
    label: 'Done',
    color: '#20b147',
    icon: CheckCircleIcon,
    rotate: 0,
  },
  {
    status: TerminalRouteStatus.Pickup,
    label: 'Pick up',
    color: '#77afe0',
    icon: StartIcon,
    rotate: -90,
  },
  {
    status: TerminalRouteStatus.HandedOver,
    label: 'Hand over',
    color: '#4192d7',
    icon: StartIcon,
    rotate: 90,
  },
  {
    status: TerminalRouteStatus.Departure,
    label: 'Departure',
    color: '#9fa4b0',
    icon: LocalShippingIcon,
    rotate: 0,
  },
  {
    status: TerminalRouteStatus.UnplannedRoute,
    label: 'Unplanned Route',
    color: '#f72c25',
    icon: FmdBadIcon,
    rotate: 0,
  },
] as const;

export const TERMINAL_STATUS_FALLBACK_OPTION = {
  status: 'Default',
  label: 'Status',
  color: '#9aa4b2',
  icon: RadioButtonUncheckedOutlinedIcon,
  rotate: 0,
};
/**
 * Type guard to validate a value is a valid TerminalRouteStatus.
 */
export function isTerminalRouteStatus(
  value: unknown,
): value is TerminalRouteStatus {
  return TERMINAL_STATUS_OPTIONS.some((option) => option.value === value);
}

/**
 * Extract valid status values from a mixed array.
 * Returns either the filtered values or all defaults if input is invalid.
 */
export function getTerminalStatusSelections(
  value: unknown,
): TerminalRouteStatus[] {
  if (Array.isArray(value)) {
    return value.filter(isTerminalRouteStatus);
  }

  return TERMINAL_STATUS_OPTIONS.map((option) => option.value);
}

/**
 * Extract valid "done" status values from a mixed array.
 * Returns empty array if input is invalid (nothing hidden by default).
 */
export function getDoneStatusSelections(value: unknown): TerminalRouteStatus[] {
  if (Array.isArray(value)) {
    return value.filter(isTerminalRouteStatus);
  }

  return [];
}

const TERMINAL_STATUS_DESCRIPTION_MAP: Record<TerminalRouteStatus, string> = {
  [TerminalRouteStatus.NonArrival]: 'NON ARRIVAL AT',
  [TerminalRouteStatus.Arrived]: 'ARRIVED AT',
  [TerminalRouteStatus.Ongoing]: 'ONGOING AT',
  [TerminalRouteStatus.ReadyForPu]: 'READY FOR PU AT',
  [TerminalRouteStatus.Done]: 'DONE AT',
  [TerminalRouteStatus.Pickup]: 'PICKED UP AT',
  [TerminalRouteStatus.HandedOver]: 'HANDED OVER AT',
  [TerminalRouteStatus.Departure]: 'DEPARTED FROM',
  [TerminalRouteStatus.UnplannedRoute]: 'UNPLANNED ROUTE AT',
};

/**
 * Format a terminal status description from a log entry, mirroring the
 * server-side `formatTerminalStatusDescriptionFromLog` output.
 */
export function formatTerminalStatusDescriptionFromLog(
  log?: {
    status?: TerminalRouteStatus | string | null;
    locationName?: string | null;
    locationLabel?: string | null;
    gate?: string | null;
  } | null,
): string {
  if (!log || !log.status || !isTerminalRouteStatus(log.status)) {
    return '';
  }

  const locationDisplayName = (
    log.locationName?.trim() ||
    log.locationLabel?.trim() ||
    ''
  ).toUpperCase();
  const gateSuffix = log.gate ? ` - ${log.gate}` : '';
  const prefix = TERMINAL_STATUS_DESCRIPTION_MAP[log.status] ?? '';

  return `${prefix} ${locationDisplayName}${gateSuffix}`;
}

/**
 * Find the latest active terminal status log entry for the given terminal id.
 */
export type TerminalStatusLogLike = {
  terminalId?: number | null;
  locationId?: number | null;
  timestamp?: string | null;
  deletedAt?: string | null;
  status?: TerminalRouteStatus | string | null;
  locationName?: string | null;
  locationLabel?: string | null;
  gate?: string | null;
};

export function getLatestTerminalStatusLogForTerminal(
  logs: TerminalStatusLogLike[] | null | undefined,
  locationId: number,
): TerminalStatusLogLike | null {
  if (!Array.isArray(logs) || !logs.length || !locationId) {
    return null;
  }

  let latest: TerminalStatusLogLike | null = null;
  let latestTime = -Infinity;

  for (const log of logs) {
    const logLocationId = log?.locationId ?? log?.terminalId;
    if (!log || logLocationId !== locationId || log.deletedAt) {
      continue;
    }

    const time = log.timestamp ? new Date(log.timestamp).getTime() : NaN;
    if (Number.isNaN(time)) {
      continue;
    }

    if (latest === null || time >= latestTime) {
      latest = log;
      latestTime = time;
    }
  }

  return latest;
}
